import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { X, Megaphone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface Announcement {
  id: string;
  text: string;
}

export default function AnnouncementBanner() {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const textRef = useRef<HTMLSpanElement | null>(null);
  const [shouldScroll, setShouldScroll] = useState(false);

  useEffect(() => {
    supabase
      .from("announcements")
      .select("id, text")
      .eq("enabled", true)
      .order("created_at", { ascending: false })
      .limit(1)
      .then(({ data }) => {
        if (data && data.length > 0) {
          const a = data[0];
          const wasDismissed = localStorage.getItem(`announcement_dismissed_${a.id}`) === "true";
          if (!wasDismissed) setAnnouncement(a);
        }
      });
  }, []);

  useLayoutEffect(() => {
    if (!announcement || dismissed) return;

    const update = () => {
      const c = containerRef.current;
      const t = textRef.current;
      if (!c || !t) return;
      setShouldScroll(t.scrollWidth > c.clientWidth);
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [announcement, dismissed]);

  if (!announcement || dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(`announcement_dismissed_${announcement.id}`, "true");
    setDismissed(true);
  };

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 border border-primary/20 text-sm">
      <Megaphone className="h-4 w-4 text-primary shrink-0" />
      <div ref={containerRef} className="flex-1 overflow-hidden">
        <div
          className={cn(
            "flex w-max whitespace-nowrap text-foreground/90",
            shouldScroll && "animate-marquee motion-reduce:animate-none"
          )}
        >
          <span ref={textRef} className={cn(!shouldScroll && "truncate block w-full")}>
            {announcement.text}
          </span>
          {shouldScroll && (
            <>
              <span className="px-10" />
              <span aria-hidden="true">{announcement.text}</span>
            </>
          )}
        </div>
      </div>
      <button
        onClick={handleDismiss}
        className="ml-auto shrink-0 text-muted-foreground hover:text-foreground transition-colors"
        aria-label="关闭公告"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
